import React, { useState } from 'react';
import { 
  AlertTriangle, Phone, User, MapPin, Clock, CheckCircle, Shield
} from 'lucide-react';
import GlassPanel from '../components/GlassPanel';
import EmptyState from '../components/EmptyState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Emergency type config
const emergencyTypes = {
  burst_pipe: { label: 'Burst Pipe', icon: '💧' },
  electrics_out: { label: 'Electrics Out', icon: '⚡' },
  gas_issue: { label: 'Gas Issue', icon: '🔥' },
  heating_failure: { label: 'Heating Failure', icon: '❄️' },
  structural_safety: { label: 'Structural Safety', icon: '🏠' },
  other: { label: 'Other Emergency', icon: '⚠️' }
};

// Status labels as per spec
const statusLabels = {
  reported: 'Reported',
  escalating: 'Escalating',
  awaiting_acceptance: 'Awaiting Trade Acceptance',
  accepted: 'Trade Accepted',
  in_progress: 'In Progress',
  resolved: 'Resolved'
};

// Mock active emergencies
const mockEmergencies = [
  {
    id: 'em-001',
    emergency_type: 'burst_pipe',
    description: 'Main water pipe burst under kitchen sink. Water flooding kitchen floor.',
    status: 'escalating',
    contact_name: 'Mike Thompson',
    contact_phone: '07700 900123',
    address: '45 Oak Street, London, SW1 2AB',
    created_at: new Date(Date.now() - 3600000).toISOString(),
    timeline: [
      { id: '1', action: 'Emergency received', timestamp: new Date(Date.now() - 3600000).toISOString() },
      { id: '2', action: 'Owner notified', timestamp: new Date(Date.now() - 3540000).toISOString() },
      { id: '3', action: 'On-call trade notified', timestamp: new Date(Date.now() - 3500000).toISOString() },
      { id: '4', action: 'Retry / escalation attempt', timestamp: new Date(Date.now() - 3400000).toISOString() },
    ]
  }
];

export function EmergencyMode() {
  const [emergencies, setEmergencies] = useState(mockEmergencies);
  const [selectedEmergency, setSelectedEmergency] = useState(null);

  const formatTimestamp = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-GB', { 
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  // Trade accepts emergency
  const handleAccept = (emergencyId) => {
    setEmergencies(prev => prev.map(e => {
      if (e.id === emergencyId) {
        return { 
          ...e, 
          status: 'accepted',
          timeline: [...e.timeline, {
            id: String(e.timeline.length + 1),
            action: 'Trade acceptance',
            timestamp: new Date().toISOString()
          }]
        };
      }
      return e;
    }));
  };

  return (
    <div className="page" data-testid="emergency-mode">
      {/* Emergency Header with Red Banner */}
      <div style={{
        background: emergencies.length > 0 ? 'var(--status-danger)' : 'var(--glass-bg)',
        color: emergencies.length > 0 ? 'white' : 'var(--text-primary)',
        padding: 'var(--space-lg)',
        borderRadius: 'var(--radius-lg)',
        marginBottom: 'var(--space-xl)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }} data-testid="emergency-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
          <AlertTriangle size={28} />
          <div>
            <h1 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>Emergency Mode</h1>
            <p style={{ margin: 0, opacity: 0.9, fontSize: '0.875rem' }}>
              {emergencies.length > 0 
                ? `${emergencies.length} active emergency`
                : 'No active emergencies'
              }
            </p>
          </div>
        </div>
        {emergencies.length > 0 && (
          <div style={{ 
            background: 'rgba(255,255,255,0.2)', 
            padding: 'var(--space-sm) var(--space-md)',
            borderRadius: 'var(--radius-md)',
            fontSize: '0.75rem',
            fontWeight: '600'
          }}>
            Escalation Active
          </div>
        )}
      </div>

      {/* Active Emergencies */}
      {emergencies.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
          {emergencies.map(emergency => {
            const typeConfig = emergencyTypes[emergency.emergency_type];
            
            return (
              <div 
                key={emergency.id}
                className="glass-panel"
                style={{
                  border: '2px solid var(--status-danger)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                data-testid={`emergency-${emergency.id}`}
              >
                {/* Red Banner at top */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'var(--status-danger)'
                }} />
                
                <div style={{ display: 'flex', gap: 'var(--space-xl)', paddingTop: 'var(--space-sm)' }}>
                  {/* Emergency Icon */}
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: 'var(--radius-lg)',
                    background: 'var(--status-danger-bg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.75rem',
                    flexShrink: 0
                  }}>
                    {typeConfig?.icon || '⚠️'}
                  </div>
                  
                  {/* Emergency Details */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-sm)' }}>
                      <h3 style={{ margin: 0, fontSize: '1.125rem', color: 'var(--status-danger)' }}>
                        {typeConfig?.label || 'Emergency'}
                      </h3>
                      {/* Status Label */}
                      <span style={{
                        fontSize: '0.625rem',
                        padding: '4px 10px',
                        borderRadius: 'var(--radius-full)',
                        background: 'var(--status-danger)',
                        color: 'white',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        {statusLabels[emergency.status] || emergency.status}
                      </span>
                    </div>
                    
                    <p style={{ margin: '0 0 var(--space-md)', color: 'var(--text-secondary)', fontSize: '0.9375rem' }}>
                      {emergency.description}
                    </p>
                    
                    {/* Contact Details */}
                    <div style={{ display: 'flex', gap: 'var(--space-xl)', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                        <User size={14} />
                        {emergency.contact_name}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                        <Phone size={14} />
                        {emergency.contact_phone}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                        <MapPin size={14} />
                        {emergency.address}
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions - Accept only (no Resolve per spec) */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', alignItems: 'flex-end' }}>
                    {emergency.status !== 'accepted' && emergency.status !== 'resolved' && (
                      <button 
                        className="btn btn-primary"
                        onClick={() => handleAccept(emergency.id)}
                        data-testid={`accept-btn-${emergency.id}`}
                      >
                        <CheckCircle size={16} />
                        Accept
                      </button>
                    )}
                    {emergency.status === 'accepted' && (
                      <span style={{
                        fontSize: '0.75rem',
                        padding: '6px 12px',
                        borderRadius: 'var(--radius-md)',
                        background: 'var(--status-success-bg)',
                        color: 'var(--status-success)',
                        fontWeight: '600'
                      }}>
                        Accepted
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Emergency Timeline - Immutable, visible, timestamped */}
                <div style={{ 
                  marginTop: 'var(--space-lg)',
                  paddingTop: 'var(--space-md)',
                  borderTop: '1px solid var(--glass-border)'
                }}>
                  <div style={{ 
                    fontSize: '0.6875rem', 
                    color: 'var(--text-muted)', 
                    marginBottom: 'var(--space-sm)',
                    textTransform: 'uppercase',
                    fontWeight: '600',
                    letterSpacing: '0.05em'
                  }}>
                    Emergency Timeline (Immutable)
                  </div>
                  
                  {/* Timeline entries - all visible, timestamped, cannot be hidden */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
                    {emergency.timeline.map((entry) => (
                      <div 
                        key={entry.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-md)',
                          padding: 'var(--space-sm) var(--space-md)',
                          background: 'var(--bg-tertiary)',
                          borderRadius: 'var(--radius-sm)',
                          borderLeft: '3px solid var(--status-danger)'
                        }}
                      >
                        <Clock size={12} style={{ color: 'var(--text-dimmed)', flexShrink: 0 }} />
                        <span style={{ fontSize: '0.8125rem', color: 'var(--text-primary)', flex: 1 }}>
                          {entry.action}
                        </span>
                        {/* Timestamp visible on all entries */}
                        <span style={{ fontSize: '0.6875rem', color: 'var(--text-dimmed)', flexShrink: 0 }}>
                          {formatTimestamp(entry.timestamp)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <GlassPanel>
          <EmptyState 
            title="No Active Emergencies"
            description="Emergency reports from clients will appear here when triggered via Client Portal."
          />
        </GlassPanel>
      )}

      {/* Guardrails Notice - Non-negotiable */}
      <div style={{
        marginTop: 'var(--space-xl)',
        padding: 'var(--space-lg)',
        background: 'var(--glass-bg)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--glass-border)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--space-md)'
      }} data-testid="guardrails-notice">
        <Shield size={20} style={{ color: 'var(--accent-primary)', flexShrink: 0, marginTop: '2px' }} />
        <div>
          <h4 style={{ margin: '0 0 var(--space-sm)', color: 'var(--text-primary)', fontSize: '0.9375rem' }}>
            Emergency Mode Guardrails
          </h4>
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
            AI assistance is disabled during emergencies. The system will <strong>NOT</strong>: 
            quote prices, authorise works, give technical advice, or promise attendance times. 
            All actions are logged in an immutable timeline that cannot be edited or deleted.
          </p>
        </div>
      </div>
    </div>
  );
}

export default EmergencyMode;
